
import React from "react";
import {
  Calendar,
  Clock,
  MapPin,
  Trophy,
  ShieldCheck,
} from "lucide-react";

export default function MatchCard({ data }) {

  const matchStatus =
    data?.match_status || "Upcoming";

  const isFinished =
    matchStatus?.toLowerCase() === "finished";

  const isLive =
    data?.match_live === "1";

  return (
    <div className="mt-6">
      <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">

        {/* Top Gradient */}
        <div className="h-1 w-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />

        <div className="p-4 sm:p-6">

          {/* Header */}
          <div className="mb-8 flex flex-wrap items-center justify-between gap-4">

            {/* League Info */}
            <div className="flex items-center gap-3">

              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl border border-gray-200 bg-gray-100 p-2 dark:border-gray-700 dark:bg-gray-800">
                <img
                  src={data?.league_logo}
                  alt={data?.league_name}
                  className="h-full w-full object-contain"
                />
              </div>

              <div>
                <h2 className="text-lg font-bold bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
                  {data?.league_name}
                </h2>

                <div className="mt-1 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">

                  {data?.country_logo && (
                    <img
                      src={data?.country_logo}
                      alt={data?.country_name}
                      className="h-4 w-4 rounded-full object-cover"
                    />
                  )}

                  <span>
                    ⚽ Football • {data?.league_year}
                  </span>
                </div>
              </div>
            </div>

            {/* Match Status */}
            <div>
              {isLive ? (
                <span className="flex items-center gap-2 rounded-full bg-red-100 px-4 py-2 text-sm font-semibold text-red-600 dark:bg-red-500/20 dark:text-red-400">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
                  </span>

                  LIVE
                </span>
              ) : (
                <span
                  className={`rounded-full px-4 py-2 text-sm font-semibold ${
                    isFinished
                      ? "bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-400"
                      : "bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400"
                  }`}
                >
                  {matchStatus}
                </span>
              )}
            </div>
          </div>

          {/* Teams Section */}
          <div className="flex items-center justify-between gap-2 py-4">

            {/* Home Team */}
            <div className="flex flex-1 flex-col items-center gap-4 text-center">

              <div className="relative flex h-24 w-24 items-center justify-center rounded-3xl border border-gray-200 bg-gray-100 p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800 md:h-28 md:w-28">

                <img
                  src={data?.team_home_badge}
                  alt={data?.match_hometeam_name}
                  className="h-16 w-16 object-contain md:h-20 md:w-20"
                />

                {/* Formation */}
                <span className="absolute -bottom-2 rounded-full bg-blue-500 px-3 py-1 text-xs font-semibold text-white shadow-lg">
                  {data?.match_hometeam_system}
                </span>
              </div>

              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white md:text-xl">
                  {data?.match_hometeam_name}
                </h3>

                <p className="mt-2 text-3xl font-extrabold text-blue-500 md:text-5xl">
                  {data?.match_hometeam_score}
                </p>
              </div>
            </div>

            {/* Center Score */}
            <div className="px-2 sm:px-6">

              <div className="flex flex-col items-center gap-3">

                <div className="flex h-16 w-16 items-center justify-center rounded-full border border-blue-300 bg-gradient-to-br from-blue-500/20 to-purple-500/20 backdrop-blur">

                  <span className="text-lg font-bold text-gray-700 dark:text-gray-200">
                    VS
                  </span>
                </div>

                {data?.stage_name && (
                  <div className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                    {data?.stage_name}
                  </div>
                )}
              </div>
            </div>

            {/* Away Team */}
            <div className="flex flex-1 flex-col items-center gap-4 text-center">

              <div className="relative flex h-24 w-24 items-center justify-center rounded-3xl border border-gray-200 bg-gray-100 p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800 md:h-28 md:w-28">

                <img
                  src={data?.team_away_badge}
                  alt={data?.match_awayteam_name}
                  className="h-16 w-16 object-contain md:h-20 md:w-20"
                />

                {/* Formation */}
                <span className="absolute -bottom-2 rounded-full bg-purple-500 px-3 py-1 text-xs font-semibold text-white shadow-lg">
                  {data?.match_awayteam_system}
                </span>
              </div>

              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white md:text-xl">
                  {data?.match_awayteam_name}
                </h3>

                <p className="mt-2 text-3xl font-extrabold text-purple-500 md:text-5xl">
                  {data?.match_awayteam_score}
                </p>
              </div>
            </div>
          </div>

          {/* Match Details */}
          <div className="mt-8 grid grid-cols-1 gap-4 border-t border-gray-200 pt-5 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-4">

            {/* Date */}
            <div className="flex items-center gap-3 rounded-2xl bg-gray-50 p-4 dark:bg-gray-800/60">

              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400">
                <Calendar className="h-5 w-5" />
              </div>

              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Match Date
                </p>

                <p className="font-semibold text-gray-900 dark:text-white">
                  {data?.match_date}
                </p>
              </div>
            </div>

            {/* Time */}
            <div className="flex items-center gap-3 rounded-2xl bg-gray-50 p-4 dark:bg-gray-800/60">

              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-100 text-purple-600 dark:bg-purple-500/20 dark:text-purple-400">
                <Clock className="h-5 w-5" />
              </div>

              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Match Time
                </p>

                <p className="font-semibold text-gray-900 dark:text-white">
                  {data?.match_time}
                </p>
              </div>
            </div>

            {/* Stadium */}
            <div className="flex items-center gap-3 rounded-2xl bg-gray-50 p-4 dark:bg-gray-800/60">

              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-400">
                <MapPin className="h-5 w-5" />
              </div>

              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Stadium
                </p>

                <p className="font-semibold text-gray-900 dark:text-white">
                  {data?.match_stadium}
                </p>
              </div>
            </div>

            {/* League */}
            <div className="flex items-center gap-3 rounded-2xl bg-gray-50 p-4 dark:bg-gray-800/60">

              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 text-orange-600 dark:bg-orange-500/20 dark:text-orange-400">
                <Trophy className="h-5 w-5" />
              </div>

              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Competition
                </p>

                <p className="font-semibold text-gray-900 dark:text-white">
                  {data?.country_name}
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Status */}
          <div className="mt-6 flex items-center justify-center">

            <div className="flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">

              <ShieldCheck className="h-4 w-4 text-green-500" />

              {isFinished
                ? "Match Completed Successfully"
                : "Match Information Available"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}